import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useClinic } from '../contexts/ClinicContext';
import axios from 'axios';
import {
  LayoutDashboard, Calendar, FileText, Send, Users, Building2, Settings,
  ChevronLeft, ChevronRight, LogOut, UserCog, Shield, Activity, Calculator, Mic
} from 'lucide-react';
import API_BASE_URL from '../config/api';


function Sidebar({ collapsed, onToggle }) {
  const { user, logout } = useAuth();
  const { clinics, activeClinicId, activeClinic, canSwitch, switchClinic } = useClinic();
  const navigate = useNavigate();
  const location = useLocation();
  const [outboxCount, setOutboxCount] = useState(0);

  useEffect(() => {
    fetchOutboxCount();
    const interval = setInterval(fetchOutboxCount, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchOutboxCount = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/outbox/count`);
      setOutboxCount(res.data.count || 0);
    } catch {
      // Outbox not available yet
    }
  };

  const navItems = [
    { path: '/diary', icon: Calendar, label: 'Clinic Diary' },
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/letters', icon: FileText, label: 'Letters' },
    { path: '/outbox', icon: Send, label: 'Outbox', badge: outboxCount },
    { path: '/contacts', icon: Users, label: 'Contacts' },
    { path: '/clinics', icon: Building2, label: 'Clinics' },
    { path: '/scribe', icon: Mic, label: 'Scribe' },
    ...(user?.canViewACC ? [
      { path: '/acc-assessment', icon: Shield, label: 'ACC Assessment' },
      { path: '/tbi14', icon: Activity, label: 'TBI14' },
      { path: '/impairment', icon: Calculator, label: 'Impairment Tools' },
    ] : []),
  ];

  const adminItems = [
    { path: '/preferences', icon: Settings, label: 'Preferences' },
    { path: '/users', icon: UserCog, label: 'Users' },
  ];

  const isActive = (path) => location.pathname === path || (path !== '/' && location.pathname.startsWith(path + '/'));

  const handleNav = (path) => {
    navigate(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div style={{
      width: collapsed ? '64px' : '220px',
      minHeight: '100vh',
      backgroundColor: '#1e293b',
      color: '#e2e8f0',
      display: 'flex',
      flexDirection: 'column',
      transition: 'width 0.2s ease',
      position: 'fixed',
      left: 0,
      top: 0,
      bottom: 0,
      zIndex: 50
    }}>
      {/* Header */}
      <div style={{
        padding: collapsed ? '16px 12px' : '16px 16px',
        borderBottom: '1px solid #334155',
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'space-between'
      }}>
        {!collapsed && (
          <span style={{ fontWeight: '700', fontSize: '18px', color: '#38bdf8' }}>ClinHx</span>
        )}
        <button
          onClick={onToggle}
          style={{
            background: 'none',
            border: 'none',
            color: '#94a3b8',
            cursor: 'pointer',
            padding: '4px',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
      </div>

      {/* Clinic Switcher */}
      {!collapsed && clinics.length > 1 && (
        <div style={{ padding: '8px 12px', borderBottom: '1px solid #334155' }}>
          <select
            value={activeClinicId || ''}
            onChange={(e) => switchClinic(e.target.value)}
            disabled={!canSwitch}
            style={{
              width: '100%',
              padding: '6px 8px',
              backgroundColor: '#334155',
              color: '#e2e8f0',
              border: '1px solid #475569',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: canSwitch ? 'pointer' : 'default',
              appearance: 'auto'
            }}
          >
            {clinics.map(c => (
              <option key={c.id} value={c.id}>{c.city || c.name}</option>
            ))}
          </select>
        </div>
      )}
      {collapsed && clinics.length > 1 && activeClinic && (
        <div style={{ padding: '8px 0', borderBottom: '1px solid #334155', textAlign: 'center' }}
          title={activeClinic.name}>
          <span style={{ fontSize: '10px', fontWeight: '700', color: '#38bdf8' }}>
            {(activeClinic.city || activeClinic.name || '').substring(0, 3).toUpperCase()}
          </span>
        </div>
      )}

      {/* Nav Items */}
      <nav style={{ flex: 1, padding: '8px', overflowY: 'auto' }}>
        {navItems.map(item => (
          <button
            key={item.path}
            onClick={() => handleNav(item.path)}
            title={collapsed ? item.label : undefined}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: collapsed ? '10px 0' : '10px 12px',
              justifyContent: collapsed ? 'center' : 'flex-start',
              marginBottom: '2px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: isActive(item.path) ? '600' : '400',
              color: isActive(item.path) ? '#ffffff' : '#94a3b8',
              backgroundColor: isActive(item.path) ? '#334155' : 'transparent',
              transition: 'all 0.15s ease',
              position: 'relative'
            }}
            onMouseEnter={(e) => {
              if (!isActive(item.path)) {
                e.currentTarget.style.backgroundColor = '#283548';
                e.currentTarget.style.color = '#e2e8f0';
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive(item.path)) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#94a3b8';
              }
            }}
          >
            <item.icon size={20} />
            {!collapsed && <span>{item.label}</span>}
            {item.badge > 0 && (
              <span style={{
                position: collapsed ? 'absolute' : 'static',
                top: collapsed ? '4px' : undefined,
                right: collapsed ? '8px' : undefined,
                marginLeft: collapsed ? 0 : 'auto',
                backgroundColor: '#ef4444',
                color: 'white',
                fontSize: '11px',
                fontWeight: '600',
                padding: '1px 6px',
                borderRadius: '10px',
                minWidth: '18px',
                textAlign: 'center'
              }}>
                {item.badge}
              </span>
            )}
          </button>
        ))}

        {/* Admin section */}
        {user?.role === 'admin' && (
          <>
            <div style={{
              margin: '12px 0 8px',
              padding: collapsed ? '0' : '0 12px',
              borderTop: '1px solid #334155',
              paddingTop: '12px'
            }}>
              {!collapsed && (
                <span style={{ fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: '600' }}>
                  Admin
                </span>
              )}
            </div>
            {adminItems.map(item => (
              <button
                key={item.path}
                onClick={() => handleNav(item.path)}
                title={collapsed ? item.label : undefined}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: collapsed ? '10px 0' : '10px 12px',
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  marginBottom: '2px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: isActive(item.path) ? '600' : '400',
                  color: isActive(item.path) ? '#ffffff' : '#94a3b8',
                  backgroundColor: isActive(item.path) ? '#334155' : 'transparent',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  if (!isActive(item.path)) {
                    e.currentTarget.style.backgroundColor = '#283548';
                    e.currentTarget.style.color = '#e2e8f0';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive(item.path)) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#94a3b8';
                  }
                }}
              >
                <item.icon size={20} />
                {!collapsed && <span>{item.label}</span>}
              </button>
            ))}
          </>
        )}
      </nav>

      {/* User + Logout */}
      <div style={{
        padding: collapsed ? '12px 8px' : '12px 16px',
        borderTop: '1px solid #334155',
        flexShrink: 0
      }}>
        {!collapsed && user && (
          <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user.name || user.email}
          </div>
        )}
        <button
          onClick={handleLogout}
          title={collapsed ? 'Logout' : undefined}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: collapsed ? '10px 0' : '10px 12px',
            justifyContent: collapsed ? 'center' : 'flex-start',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            fontSize: '14px',
            color: '#94a3b8',
            backgroundColor: 'transparent',
            transition: 'all 0.15s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#283548';
            e.currentTarget.style.color = '#f87171';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = '#94a3b8';
          }}
        >
          <LogOut size={20} />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </div>
  );
}

export default Sidebar;
